import React, { useState, useEffect, useRef } from 'react';
import { Play, Plus, Info, Check, Clock, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { myListApi, formatDuration, thumbUrl } from '../lib/api';
import { toTitleCase } from '../lib/utils';

export const HeroSection = ({ films, onCreatorClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inMyList, setInMyList] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const bgRef = useRef(null);
  const fgRef = useRef(null);

  const currentFilm = films[currentIndex];

  // Smooth parallax via refs + rAF (adaptive intensity for mobile)
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const bgSpeed = isMobile ? 0.15 : 0.35;
    const fgSpeed = isMobile ? -0.05 : -0.12;
    let rafId = null;
    let ticking = false;
    function update() {
      const y = window.scrollY;
      if (bgRef.current) bgRef.current.style.transform = 'translate3d(0,' + y * bgSpeed + 'px,0)';
      if (fgRef.current) fgRef.current.style.transform = 'translate3d(0,' + y * fgSpeed + 'px,0)';
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { rafId = requestAnimationFrame(update); ticking = true; }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', onScroll); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  useEffect(() => {
    if (films.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % films.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [films.length]);

  useEffect(() => {
    const checkMyList = async () => {
      if (!isAuthenticated || !currentFilm) return;
      try {
        const result = await myListApi.check(currentFilm.film_id);
        setInMyList(result.in_list);
      } catch (error) {
        console.error('Failed to check my list:', error);
      }
    };
    checkMyList();
  }, [currentFilm, isAuthenticated]);

  const handleAddToList = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      if (inMyList) {
        await myListApi.remove(currentFilm.film_id);
        setInMyList(false);
      } else {
        await myListApi.add(currentFilm.film_id);
        setInMyList(true);
      }
    } catch (error) {
      console.error('Failed to update my list:', error);
    }
  };

  const handleDescriptionClick = () => {
    navigate(`/film/${currentFilm.film_id}`);
  };

  if (!currentFilm) return null;

  return (
    <section className="relative w-full min-h-[80vh] sm:h-[700px] md:h-[85vh] md:max-h-[900px] overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        ref={bgRef}
        style={{ backgroundImage: `url(${thumbUrl(currentFilm.thumbnail, 1280)})`, willChange: 'transform' }}
        data-testid="hero-background"
      />

      {/* Gradient Overlays - light overall, stronger at bottom-left for text */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/10 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#030014]/40 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[45%] bg-gradient-to-t from-[#030014]/90 via-[#030014]/50 to-transparent" />

      {/* Content: relative on mobile (drives section height), absolute on sm+ (fills fixed height) */}
      <div className="relative sm:absolute sm:inset-0 z-10 flex flex-col justify-end pt-16 sm:pt-0 pb-8 sm:pb-16 md:pb-20 min-h-[560px] sm:min-h-0 w-full px-4 md:px-8 lg:px-[4%]" ref={fgRef} style={{ willChange: 'transform' }}>
        <div className="max-w-2xl space-y-2 sm:space-y-3 fade-in">
          {/* Genre Tags */}
          <div className="flex flex-wrap gap-2">
            {currentFilm.genres.slice(0, 3).map((genre) => (
              <span key={genre} className="badge">{genre}</span>
            ))}
          </div>

          {/* Title - max 2 lines, auto-resize */}
          <h1
            className="font-bold tracking-tighter leading-[1.15] sm:leading-tight line-clamp-2 text-[clamp(1.5rem,5vw,1.875rem)] sm:text-4xl md:text-5xl lg:text-6xl"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="hero-title"
          >
            {toTitleCase(currentFilm.title)}
          </h1>

          {/* Metadata */}
          <div className="flex items-center gap-4 text-sm text-gray-300">
            {currentFilm.avg_rating > 0 && (
              <span className="text-[#00FF9D] font-semibold">
                {currentFilm.avg_rating.toFixed(1)} ★
              </span>
            )}
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{
              currentFilm.year > 0
                ? currentFilm.year
                : currentFilm.release_date
                  ? new Date(currentFilm.release_date).getFullYear()
                  : currentFilm.created_at
                    ? new Date(currentFilm.created_at).getFullYear()
                    : new Date().getFullYear()
            }</span>
          </div>

          {/* Description - truncated, clickable to open More Info */}
          <p
            className="text-sm md:text-base text-gray-300 line-clamp-2 cursor-pointer hover:text-gray-100 transition-colors"
            onClick={handleDescriptionClick}
            title="Click for more info"
            data-testid="hero-description"
          >
            {currentFilm.description}
          </p>

          {/* Creator */}
          <p className="text-sm text-gray-400">
            Created by{' '}
            <button
              onClick={() => onCreatorClick?.(currentFilm.creator)}
              className="text-[#A78BFA] hover:text-[#8B5CF6] hover:underline transition-colors cursor-pointer"
              data-testid="hero-creator-link"
            >
              {currentFilm.creator}
            </button>
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            <button onClick={() => navigate(`/watch/${currentFilm.film_id}`)} className="btn-white flex items-center gap-2" data-testid="hero-play-button">
              <Play className="w-5 h-5 fill-current" /> Play
            </button>
            <button onClick={() => navigate(`/film/${currentFilm.film_id}`)} className="btn-secondary flex items-center gap-2" data-testid="hero-info-button">
              <Info className="w-5 h-5" /> More Info
            </button>
            <button onClick={handleAddToList} className="btn-secondary flex items-center gap-2" data-testid="hero-mylist-button">
              {inMyList ? (<><Check className="w-5 h-5" /> In My List</>) : (<><Plus className="w-5 h-5" /> My List</>)}
            </button>
          </div>
        </div>

        {/* Film Selector Dots */}
        {films.length > 1 && (
          <div className="flex gap-2 mt-6 mb-8 sm:mb-0" data-testid="hero-dots">
            {films.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${index === currentIndex ? 'bg-white w-8' : 'bg-white/30 hover:bg-white/50'}`}
                data-testid={`hero-dot-${index}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
